/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderNavigationService } from '@gh/builder-shared';
import { AelEvents, BuilderPermission } from '@gh/builder-shared-data';
import { getRouteInitData, UserContext } from '@gh/common-shared';
import {
  Confirmation,
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
} from '@gh/core-ui';
import { filter$, switchMap$ } from '@gh/rx/operators';
import {
  Printer,
  PrinterDetailsDialogConfig,
  Printers,
  SystemVarsPrinterListTabBusinessService,
} from '../../resources';
import { printerDetailsDialogRoute } from './system-vars-printer-details-dialog/system-vars-printer-details-dialog.component';
import { assign } from 'lodash';

@Component({
  standalone: false,
  selector: 'gh-system-vars-printer-list-tab',
  templateUrl: 'system-vars-printer-list-tab.component.html',
})
export class SystemVarsPrinterListTabComponent implements OnInit {
  cloudPrinters: Printer[];
  isCloud: boolean;
  selectedPrintType: string;
  isChangedValue: boolean = false;

  constructor(private route: ActivatedRoute,
              private userContext: UserContext,
              private waitingService: WaitingService,
              private notifications: NotificationService,
              private dialogs: DialogService,
              private navigationService: BuilderNavigationService,
              private business: SystemVarsPrinterListTabBusinessService) {

  }

  ngOnInit(): void {
    const { isCloud, cloudPrinters } = getRouteInitData(this.route);
    assign(this, {isCloud, cloudPrinters});
    this.selectedPrintType = !isCloud ? 'standard' : 'cloud';
  }

  get hasPrinters(): boolean {
    return this.cloudPrinters.length > 0;
  }

  onChangePrintType(value: string): void {
    this.isChangedValue = true;
    this.isCloud = value !== 'standard';
  }

  onDelete({ cloudPrinterId }: Printer): void {
    this.dialogs.confirmation({
      messageId: 'msg_delete_printer',
      buttons: [Confirmation.Yes, Confirmation.No],
      defaultButton: Confirmation.No,
    }).pipe(filter$(yesAnswer),
            switchMap$(() => this.business.remove(cloudPrinterId)))
      .subscribe(
        () => this.reload(),
        this.notifications.processAll);
  }

  onEdit(printer: Printer): void {
    if (this.userContext.hasPermission(BuilderPermission.Setup.SystemVariables.Printers.EDIT)) {
      this.dialogs.openWithInput(printerDetailsDialogRoute, <PrinterDetailsDialogConfig>{ printer })
        .afterClosed().pipe(
        filter$(Boolean))
        .subscribe(() => this.reload());
    }
  }

  reload(): void {
    this.business.findAll().pipe(
      (this.waitingService.operator<Printers>()))
      .subscribe(({ cloudPrinters }) => this.cloudPrinters = cloudPrinters);
  }

  onProcess(): void {
    this.business.changePrintType(this.isCloud)
      .subscribe(
        () => {
          this.notifications.success('msg_saved_successfully');
          this.isChangedValue = false;
          this.reload();
        },
        this.notifications.processAll);
  }

  goToAel(): void {
    this.navigationService.goToAel({
      label: 'lbl_cloud_printers',
      entityType: 'OFFICE',
      eventTypeIds: [AelEvents.Office.EditedPrinters],
    });
  }
}
